import { MealNeed } from '@/shared/api/mealNeeds'
import { parseLocalDate } from '@/modules/trips/utils/dateHelpers'

export interface Contributor {
  id: string
  userId: string | null
  name: string
  fullName?: string
  email?: string
  avatarUrl?: string
}

export interface WeeklyStats {
  totalMeals: number
  contributors: number
  totalServings: number
}

/**
 * Get a consistent color for a contributor based on their ID
 */
export function getContributorColor(userId: string | null, fallbackId?: string): string {
  const idToUse = userId || fallbackId || 'unknown'
  // Match screenshot colors: green, orange, blue, purple
  const colors = [
    'bg-green-100 text-green-700',
    'bg-orange-100 text-orange-700',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-adventure-100 text-adventure-700',
    'bg-earth-100 text-earth-700',
    'bg-gray-100 text-gray-700',
    'bg-pink-100 text-pink-700',
    'bg-yellow-100 text-yellow-700',
    'bg-indigo-100 text-indigo-700'
  ]
  
  // Use ID to pick a color consistently
  const hash = idToUse.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Get background color for contributor badge (solid color with white text)
 */
export function getContributorBadgeColor(userId: string | null, fallbackId?: string): string {
  const idToUse = userId || fallbackId || 'unknown'
  // Match screenshot colors: green, orange, blue, purple (solid versions)
  const colors = [
    'bg-green-600',
    'bg-orange-500',
    'bg-blue-500',
    'bg-purple-500',
    'bg-adventure-600',
    'bg-earth-600',
    'bg-gray-600',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500'
  ]
  
  // Use ID to pick a color consistently
  const hash = idToUse.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Get initials from a name
 */
export function getInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Extract unique contributors from meals
 */
export function extractContributors(meals: MealNeed[]): Contributor[] {
  const contributorMap = new Map<string, Contributor>()

  meals.forEach((meal) => {
    // Skip if meal is null/undefined
    if (!meal) return
    
    // Get contributor from created_by_user
    const userId = meal.created_by_user_id || meal.created_by_user?.id
    if (!userId) return

    // Skip if already added
    if (contributorMap.has(userId)) return

    // Get display name
    const name = 
      meal.created_by_user?.full_name ||
      meal.created_by_user?.name ||
      meal.created_by_user?.user?.full_name ||
      meal.created_by_user?.user?.name ||
      meal.created_by_user?.email?.split('@')[0] ||
      'Unknown'

    const rawFullName = meal.created_by_user?.full_name ?? meal.created_by_user?.user?.full_name
    contributorMap.set(userId, {
      id: userId,
      userId,
      name,
      fullName: rawFullName !== null ? rawFullName : undefined,
      email: meal.created_by_user?.email || meal.created_by_user?.user?.email,
      avatarUrl: meal.created_by_user?.avatar_url || meal.created_by_user?.user?.avatar_url
    })
  })

  return Array.from(contributorMap.values())
}

/**
 * Calculate weekly statistics from meals
 */
export function calculateWeeklyStats(
  meals: MealNeed[],
  startDate?: string | null,
  endDate?: string | null
): WeeklyStats {
  // Filter out null/undefined meals
  const validMeals = meals.filter((meal): meal is MealNeed => meal != null)
  
  if (!startDate || !endDate) {
    // If no date range, calculate from all meals
    const contributors = extractContributors(validMeals)
    const totalServings = validMeals.reduce((sum, meal) => sum + (meal.serves_count || 0), 0)
    
    return {
      totalMeals: validMeals.length,
      contributors: contributors.length,
      totalServings
    }
  }

  // Filter meals within the date range
  // Use parseLocalDate to avoid timezone issues when comparing date-only strings
  const start = parseLocalDate(startDate)
  const end = parseLocalDate(endDate)
  
  const mealsInRange = meals.filter((meal) => {
    if (!meal || !meal.planned_date) return false
    const mealDate = parseLocalDate(meal.planned_date)
    return mealDate >= start && mealDate <= end
  })

  const contributors = extractContributors(mealsInRange)
  const totalServings = mealsInRange.reduce((sum, meal) => {
    if (!meal) return sum
    return sum + (meal.serves_count || 0)
  }, 0)

  return {
    totalMeals: mealsInRange.length,
    contributors: contributors.length,
    totalServings
  }
}

/**
 * Get contributor name from a meal
 * Falls back to "Crew member" only if no user data is available
 */
export function getMealContributorName(meal: MealNeed): string {
  // Try to get name from created_by_user (most common)
  const name = 
    meal.created_by_user?.full_name ||
    meal.created_by_user?.name ||
    meal.created_by_user?.user?.full_name ||
    meal.created_by_user?.user?.name ||
    meal.created_by_user?.email?.split('@')[0] ||
    null

  // Only fall back to "Crew member" if we truly have no user data
  // This should be rare now that backend includes trip member info
  if (!name && meal.created_by_user_id) {
    // If we have a user ID but no name, try to construct a fallback
    // This shouldn't happen often with the backend improvements
    return 'Crew member'
  }

  return name || 'Crew member'
}

/**
 * Get contributor ID from a meal
 */
export function getMealContributorId(meal: MealNeed): string | null {
  return meal.created_by_user_id || meal.created_by_user?.id || null
}

